import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { foldName } from "@trip/domain/poi";
import { navigate, tripPath, CATEGORIES_TAB } from "../lib/router.tsx";
import { del, patch, post, postCreating, put, type Member, type Trip } from "../lib/api.ts";
import { categoryName, nextColour } from "../lib/category-icons.ts";
import {
  addCategoryNamed,
  canRemovePoi,
  editPoi,
  removePoi,
  syncPois,
  usePois,
  type Poi,
  type PoiCategory,
} from "../lib/pois.ts";
import { CLOSED_DOWN, placeDetails, searchFailed, type FoundPlace } from "../lib/place-search.ts";
import { isAdmin } from "../lib/rights.ts";
import { useApi } from "../lib/useApi.ts";
import { tabKeys, tabStop } from "../ui/tabs.ts";
import { CategoryForm, type CategoryFace } from "./CategoryForm.tsx";
import { CategoryList, NO_CATEGORY, type CategoryCount } from "./CategoryList.tsx";
import { PoiEditor } from "./PoiEditor.tsx";
import { PoiRow, poiName } from "./PoiRow.tsx";
import { PoiSync } from "./PoiSync.tsx";

type Tab = "places" | "categories";
/** Left to right, and the first is where the page opens. */
const TABS: { id: Tab; label: string }[] = [
  { id: "places", label: "Places" },
  { id: "categories", label: "Categories" },
];

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * The trip's places and categories on a page of their own, reached from Settings and from the
 * map's pencil. Two tabs, and it opens on Places unless the address names the Categories tab (the
 * pencil's does): a search over every place, each in the row the map's sheet uses, with the Google
 * import above the list. The Categories tab is the trip's category list, in the order the trip
 * keeps them, which any member may change, with a panel to add, change or remove one. A place's
 * name opens the map's own editor in a panel, with Delete only for whoever may remove it. The page
 * is for editing, so its rows carry no like and no visited control: those are the map's.
 *
 * It is also where a place Google found is added. The map's search only looks at results; its Add
 * sends the member here with the place's id in the address, and this page spends the one paid read
 * of that place, opens the editor on its name and hours, and holds its position until the save.
 * Carrying the id and not the place itself is what lets a reload land on the same filled form.
 */
export function PlacesPage({
  trip,
  tab: opensOn = "places",
  adding = null,
}: {
  trip: Trip;
  tab?: Tab;
  /**
   * The id of a place Google found, from the address, to be read and added; null for every other
   * visit. The position never travels, only this: an id is short, safe in a link, and says
   * nothing about the place, which Google is asked for on arrival.
   */
  adding?: string | null;
}) {
  const on = trip.mapLayersEnabled;
  const base = `/v1/trips/${trip.id}`;
  const list = usePois(trip.id, on);
  // One read of the members, for the colour of each face and to know which of them is the reader.
  const members = useApi<{ items: Member[] }>(on ? `${base}/members` : null);
  const [tab, setTab] = useState<Tab>(opensOn);
  // The address names the tab, so a new address (Back, or the pencil) moves it: the prop is
  // followed when it changes, and left alone while it does not, so a click is not overruled.
  const [named, setNamed] = useState<Tab>(opensOn);
  if (named !== opensOn) {
    setNamed(opensOn);
    setTab(opensOn);
  }
  const [query, setQuery] = useState("");
  /** The place in the editor panel, by id. */
  const [editingPoi, setEditingPoi] = useState<string | null>(null);
  /** The category panel: one being edited, or a new one (`id` null). */
  const [editing, setEditing] = useState<{ id: string | null } | null>(null);
  /** What the last save found instead of saving: "Already a category: ⚽ Sports". */
  const [notice, setNotice] = useState<string | null>(null);
  /** The order the server last accepted, which the list read from the server may not yet reflect. */
  const [sequence, setSequence] = useState<string[] | null>(null);
  /** The place the address named, as Google gave it: the editor opens on this. */
  const [found, setFound] = useState<FoundPlace | null>(null);
  /** Why there is no form instead: unreadable, or a place that has shut down for good. */
  const [foundError, setFoundError] = useState<string | null>(null);

  const fetched = list.categories;
  // A reorder the server confirmed stands until the next read; a category it does not name (added
  // since) goes last, where the server puts one with no position.
  const categories = useMemo(() => {
    if (!fetched || !sequence) return fetched;
    const at = (c: PoiCategory) => {
      const i = sequence.indexOf(c.id);
      return i < 0 ? sequence.length : i;
    };
    return [...fetched].sort((a, b) => at(a) - at(b));
  }, [fetched, sequence]);
  const counts = useMemo(() => {
    const out: Record<string, CategoryCount> = {};
    for (const p of list.pois ?? []) {
      const key = p.categoryId ?? NO_CATEGORY;
      const n = out[key] ?? { places: 0, visited: 0 };
      out[key] = { places: n.places + 1, visited: n.visited + (p.ticked !== null ? 1 : 0) };
    }
    return out;
  }, [list.pois]);

  /**
   * The one paid read of a place, on arrival from the map's Add. A place Google says has closed
   * down for good opens no form: there is nothing worth planning around it, and the search cannot
   * know, because it is the server that leaves those out of a search and this read that tells.
   */
  useEffect(() => {
    // Nothing is drawn for a trip without the map layers, so a stray address spends nothing.
    if (adding === null || !on) {
      setFound(null);
      setFoundError(null);
      return;
    }
    let live = true;
    setFound(null);
    setFoundError(null);
    placeDetails(trip.id, adding)
      .then((place) => {
        if (!live) return;
        if (place.status === "CLOSED_PERMANENTLY") setFoundError(CLOSED_DOWN);
        else setFound(place);
      })
      .catch((e: unknown) => {
        if (!live) return;
        console.error("places: the place Google found could not be read", e);
        setFoundError(searchFailed(e).text);
      });
    return () => {
      live = false;
    };
  }, [trip.id, adding, on]);

  if (!on) return <p className="muted">Places are not turned on for this trip.</p>;

  const { pois } = list;
  const people = members.data?.items ?? [];
  const me = people.find((m) => m.isYou)?.userId ?? null;
  const colorOf = (id: string) => people.find((m) => m.userId === id)?.color ?? null;
  const emojis = new Map(categories?.map((c) => [c.id, c.emoji]));
  // The place in the editor, as the list now has it; one deleted from under it simply closes it.
  const editedPoi = editingPoi === null ? null : (pois?.find((p) => p.id === editingPoi) ?? null);
  /** A like from the editor: its own write, the place as the server then has it put in the list. */
  async function editorVote(p: Poi, on: boolean) {
    const path = `${base}/pois/${p.id}/vote`;
    list.replace(on ? await put<Poi>(path, undefined) : await del<Poi>(path));
  }
  /**
   * A place Google found, through the create path the trip already has. The position is the one
   * from the read, not anything typed: the form never offered it, and a pair of numbers is not
   * something a member can check. Moving it later is dragging its pin on the map.
   */
  async function addFound(place: FoundPlace, draft: Parameters<typeof editPoi>[2]) {
    list.replace(
      await post<Poi>(`${base}/pois`, {
        name: draft.name,
        lat: place.lat,
        lon: place.lon,
        categoryId: draft.categoryId,
        ...(draft.note !== null ? { note: draft.note } : {}),
        windows: draft.windows,
      }),
    );
    doneAdding();
  }
  /** The form closes and the address stops naming Google's place, so coming back is the page. */
  function doneAdding() {
    setFound(null);
    setFoundError(null);
    navigate(tripPath(trip.id, "places"), { replace: true });
  }

  /** Deleted on the server's word; then it leaves the list and the panel closes. */
  async function removeEdited(id: string) {
    await removePoi(trip.id, id);
    list.remove(id);
    setEditingPoi(null);
  }

  const needle = foldName(query);
  const shown = [...(pois ?? [])]
    .filter((p) => needle === "" || foldName(p.name).includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name));

  /** Visited places are their own group, as the map's sheet has them; each stays alphabetical. */
  const todo = shown.filter((p) => p.ticked === null);
  const done = shown.filter((p) => p.ticked !== null);
  const row = (p: Poi) => (
    <PoiRow
      key={p.id}
      poi={p}
      name={poiName(p, emojis)}
      open={{ label: "Edit", run: (id) => setEditingPoi(id) }}
      visited={p.ticked !== null}
      me={me}
      colorOf={colorOf}
    />
  );

  /** Every category in its new sequence, which is what the server insists on. A refusal passes up. */
  async function reorder(ids: string[]) {
    await patch(`${base}/poi-categories/order`, { ids });
    setSequence(ids);
  }
  /** A new category or a changed one, put into the list only as the server answers. */
  async function saveCategory(face: CategoryFace) {
    if (editing?.id) {
      list.addCategory(await patch<PoiCategory>(`${base}/poi-categories/${editing.id}`, face));
    } else {
      const r = await postCreating<PoiCategory>(`${base}/poi-categories`, face);
      list.addCategory(r.body);
      // A 200 is the category the trip already had by that name, with its own face, not this one.
      setNotice(
        r.created ? null : `Already a category: ${r.body.emoji} ${categoryName(r.body.name)}`,
      );
    }
    setEditing(null);
  }
  /** Gone on the server's word; its places are left without a category, as the server leaves them. */
  async function removeCategory(id: string) {
    await del<{ uncategorised: number }>(`${base}/poi-categories/${id}`);
    list.removeCategory(id);
    list.refresh();
    setEditing(null);
  }
  const editedCategory = editing?.id
    ? (categories?.find((c) => c.id === editing.id) ?? null)
    : null;

  return (
    <div className="stack">
      <p className="muted small" style={{ margin: 0 }} aria-live="polite">
        {pois && categories
          ? `${plural(pois.length, "place", "places")} · ${plural(categories.length, "category", "categories")}`
          : list.error
            ? ""
            : "Loading places…"}
      </p>

      <div
        className="lib-tabs"
        role="tablist"
        aria-label="Places and categories"
        onKeyDown={tabKeys}
      >
        {TABS.map((t, i) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`places-tab-${t.id}`}
            aria-selected={t.id === tab}
            aria-controls={`places-panel-${t.id}`}
            tabIndex={tabStop(
              i,
              TABS.findIndex((x) => x.id === tab),
            )}
            className={`lib-tab${t.id === tab ? " on" : ""}`}
            onClick={() => {
              setTab(t.id);
              // The address follows the tab, replacing rather than pushing: Back leaves the page.
              navigate(
                tripPath(trip.id, "places", t.id === "categories" ? CATEGORIES_TAB : undefined),
                {
                  replace: true,
                },
              );
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "places" ? (
        <div
          className="stack"
          role="tabpanel"
          id="places-panel-places"
          aria-labelledby="places-tab-places"
        >
          {adding !== null && found === null && foundError === null && (
            <p className="muted small" role="status">
              Reading the place from Google…
            </p>
          )}
          {foundError && (
            <p className="error" role="alert">
              {foundError}
            </p>
          )}
          <PoiSync
            tripId={trip.id}
            onSync={async (url) => {
              const r = await syncPois(trip.id, url);
              // What the sync added or removed is on the page now, not at the next visit.
              list.refresh();
              return r;
            }}
          />
          <label className="field">
            <span className="sr">Search places</span>
            <input
              type="search"
              placeholder="Search places"
              autoComplete="off"
              enterKeyHint="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          {list.error && (
            <p role={pois ? "status" : "alert"} className={pois ? "muted small" : "error"}>
              {list.error}
            </p>
          )}
          {pois && todo.length > 0 && (
            <ul className="plain poi-rows" aria-label="Places">
              {todo.map(row)}
            </ul>
          )}
          {/* The sheet's own grouping: a heading and a list, only when there is a visited place to
              show. A search narrows this group as it does the other. */}
          {pois && done.length > 0 && (
            <>
              <h3 className="label poi-done-title">Visited</h3>
              <ul className="plain poi-rows" aria-label="Visited">
                {done.map(row)}
              </ul>
            </>
          )}
          {pois && shown.length === 0 && (
            <p className="muted">
              {needle === "" ? "No places yet." : `No place matches “${query.trim()}”.`}
            </p>
          )}
        </div>
      ) : (
        <div
          className="stack"
          role="tabpanel"
          id="places-panel-categories"
          aria-labelledby="places-tab-categories"
        >
          {categories && (
            <CategoryList
              categories={categories}
              counts={counts}
              onReorder={reorder}
              onEdit={(id) => {
                setNotice(null);
                setEditing({ id });
              }}
            />
          )}
          {!categories && (
            <p className={list.error ? "error" : "muted"} role={list.error ? "alert" : undefined}>
              {list.error ?? "Loading categories…"}
            </p>
          )}
          {categories && (
            <button
              type="button"
              className="button"
              onClick={() => {
                setNotice(null);
                setEditing({ id: null });
              }}
            >
              <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
              Add category
            </button>
          )}
          {notice && (
            <p role="status" className="small">
              {notice}
            </p>
          )}
        </div>
      )}
      {categories && editedPoi && (
        <div className="sheet-scrim places-poi-panel">
          <PoiEditor
            key={editedPoi.id}
            poi={editedPoi}
            categories={categories}
            onSave={async (draft) => {
              list.replace(await editPoi(trip.id, editedPoi, draft));
              setEditingPoi(null);
            }}
            onAddCategory={async (name) => {
              const c = await addCategoryNamed(trip.id, name, categories);
              list.addCategory(c);
              return c;
            }}
            onVote={(on) => editorVote(editedPoi, on)}
            // Offered only to whoever the server lets: the adder, or a trip admin.
            onRemove={
              canRemovePoi(editedPoi, me, isAdmin(trip.roles))
                ? () => removeEdited(editedPoi.id)
                : undefined
            }
            onClose={() => setEditingPoi(null)}
          />
        </div>
      )}
      {categories && found && (
        <div className="sheet-scrim places-poi-panel">
          <PoiEditor
            key={found.placeId}
            poi={null}
            // Google's name and hours; the category and the note are the member's to add, and are
            // the two things Google cannot give, which is what this page is for.
            initial={{ name: found.name, windows: found.windows }}
            categories={categories}
            onSave={(draft) => addFound(found, draft)}
            onAddCategory={async (name) => {
              const c = await addCategoryNamed(trip.id, name, categories);
              list.addCategory(c);
              return c;
            }}
            onClose={doneAdding}
            where={`Google puts it at (${found.lat.toFixed(4)}, ${found.lon.toFixed(4)}), with the hours it knows. Pick a category, and add a note if there is one to add.`}
          />
        </div>
      )}
      {categories && editing && (editing.id === null || editedCategory) && (
        <CategoryForm
          key={editing.id ?? "new"}
          category={editedCategory}
          colour={nextColour(categories)}
          places={counts[editing.id ?? ""]?.places ?? 0}
          onSave={saveCategory}
          onRemove={() => removeCategory(editing.id ?? "")}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
